import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Select } from 'antd';
import { useDispatch } from 'react-redux';

import * as actions from '@actions';

import { Accordion } from '@components/Clinicians/Accordion';
import { SessionFormItem } from '@components/Clinicians/Session/Components/SessionFormItem';

import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import useClinicalNotesMeta from '@hooks/useClinicalNotesMeta';
import { confirmationModal } from 'vl-common/src/components/Modals';
import { useSessionFormState } from '@hooks/useSessionFormState';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';

const RecommendationNotes = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();

  const [formValue, setFormValue] = useSessionFormState('RECOMMENDATIONS');

  const {
    appointment: { read_only: readOnly, appointment_completed: isCompleted, clinician_user_guid }
  } = useSessionAppointment();
  const appointment_id = useSessionAppointmentId();
  const { detailsCurrentAppointment, isLoading: savedNotesLoading } = useAppointmentDetails(appointment_id);
  const { data: appointmentsMeta } = useAppointmentMeta();

  const options = appointmentsMeta?.recnote_codes?.map(({ recnote_code, recnote_desc }) => {
    return {
      label: recnote_desc,
      value: recnote_code
    };
  });

  const { getIsFieldRequired, getNoteLabel } = useClinicalNotesMeta();
  const isRequired = getIsFieldRequired('RECOMMENDATIONS');

  const isV2Appointment = detailsCurrentAppointment?.appt_version === 2;

  const [isCheckedManually, setIsCheckedManually] = useState(false);

  const handleToggleAccordion = (checked: boolean) => {
    if (checked) {
      setIsCheckedManually(true);
      return;
    }

    if (!formValue) {
      setIsCheckedManually(false);
      return;
    }

    confirmationModal({
      title: t('Are you sure?'),
      content: t('Your internal recommendation notes will be deleted'),
      closable: true,
      onOk: () => {
        setFormValue([]);

        setIsCheckedManually(false);
        handleRecNoteChange({ value: [] });
      }
    });
  };

  const { mutate: handleRecNoteChange } = useMutation({
    mutationKey: ['handleRecNoteChange', appointment_id, clinician_user_guid],
    mutationFn: async ({ value, skipSanitisation }: { value: string[]; skipSanitisation?: boolean }) => {
      if (readOnly || savedNotesLoading) return;
      await dispatch(
        actions.clinicianUpdateRecommendationNotes({
          user_guid: clinician_user_guid,
          appt_id: appointment_id,
          recnotes: value.map((v) => ({ recnote_code: v }))
        })
      );
    }
  });

  const handleSelect = (selectedSafetyNet: any) => {
    setFormValue(selectedSafetyNet);
    handleRecNoteChange({ value: selectedSafetyNet });
  };

  const isExpanded = isCheckedManually || formValue?.length > 0;

  return (
    <Accordion
      isCollapsibleByCheckbox={isV2Appointment}
      key="safety-net"
      field="safety-net"
      disabled={isCompleted || readOnly}
      label={getNoteLabel('RECOMMENDATIONS')}
      required={isRequired}
      {...(isV2Appointment && { expanded: isExpanded, onChange: handleToggleAccordion })}
    >
      <SessionFormItem label={t('SafetyNetting')} required={isRequired}>
        <Select
          mode="multiple"
          data-testid="safety-netting"
          options={options}
          onChange={handleSelect}
          value={formValue}
          disabled={isCompleted || readOnly}
        />
      </SessionFormItem>
    </Accordion>
  );
};

export default RecommendationNotes;
